import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useCart } from "@/hooks/useCart";
import OrderSummary from "@/pages/checkout/components/OrderSummary";
import CustomerForm from "@/pages/checkout/components/CustomerForm";
import OrderSuccess from "@/pages/checkout/components/OrderSuccess";
import { useAuth } from "@/hooks/useAuth";
import { useRewards } from "@/hooks/useRewards";
import { supabase } from "@/lib/supabase";
import type { OrderData, DiscountState, PointsDiscountState, RedeemCouponState } from "@/pages/checkout/types";

// Platinum tier discount
const PLATINUM_DISCOUNT_PERCENT = 5;

export default function CheckoutPage() {
  const { items, removeItem } = useCart();
  const { user, isLoggedIn } = useAuth();
  const { getRewards } = useRewards();
  const navigate = useNavigate();
  const [successData, setSuccessData] = useState<OrderData | null>(null);
  const [outOfStockIds, setOutOfStockIds] = useState<number[]>([]);
  const [stockChecked, setStockChecked] = useState(false);
  const [discount, setDiscount] = useState<DiscountState>({
    code: "", percent: 0, amount: 0, status: "idle",
  });
  const [pointsDiscount, setPointsDiscount] = useState<PointsDiscountState>({
    pointsToRedeem: 0, discountAmount: 0, applied: false,
  });
  const [redeemCoupon, setRedeemCoupon] = useState<RedeemCouponState>({
    code: "", discountPercent: 0, status: "idle",
  });
  const [isPlatinum, setIsPlatinum] = useState(false);

  // Check if logged-in user is platinum
  useEffect(() => {
    if (isLoggedIn && user?.id) {
      getRewards(user.id).then((r) => {
        setIsPlatinum(r?.tier === "platinum");
      });
    }
  }, [isLoggedIn, user, getRewards]);

  // ── Live stock check on page load ──
  const checkOutOfStock = useCallback(async () => {
    if (items.length === 0) return;
    try {
      const ids = items.map((i) => i.id);
      const { data } = await supabase
        .from("products")
        .select("id, stock")
        .in("id", ids);
      if (data) {
        const oos = data
          .filter((row) => (row.stock ?? 0) <= 0)
          .map((row) => Number(row.id));
        setOutOfStockIds(oos);
      }
    } catch { /* silent */ } finally {
      setStockChecked(true);
    }
  }, [items]);

  useEffect(() => {
    checkOutOfStock();
  }, [checkOutOfStock]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Redirect if cart is empty and no success yet
  useEffect(() => {
    if (items.length === 0 && !successData) {
      const t = setTimeout(() => navigate("/"), 2000);
      return () => clearTimeout(t);
    }
  }, [items.length, successData, navigate]);

  const handleDiscountApply = (state: DiscountState) => setDiscount(state);

  const handleSuccess = (data: OrderData) => {
    setSuccessData(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-[#0a0a0a]"
      style={{ fontFamily: "'Cairo', sans-serif" }}
    >
      {/* Top Bar */}
      <header className="bg-[#0a0a0a]/95 backdrop-blur-md border-b border-amber-400/10 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors text-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line"></i>
            <span>العودة للمتجر</span>
          </Link>

          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/3f4c2435-201e-437d-afea-0ab12a1a0054.png"
              alt="الإبداع"
              className="w-9 h-9 object-contain"
            />
            <span className="text-white font-bold text-base hidden sm:block">الإبداع</span>
          </div>

          <div className="flex items-center gap-1.5 text-green-400 text-xs whitespace-nowrap">
            <i className="ri-lock-2-line"></i>
            <span className="hidden sm:inline">دفع آمن</span>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      {!successData && (
        <div className="bg-[#0f0f0f] border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-3">
            <div className="flex items-center justify-center gap-2 md:gap-4">
              {[
                { num: 1, label: "السلة", icon: "ri-shopping-cart-line", done: true },
                { num: 2, label: "إتمام الطلب", icon: "ri-file-list-3-line", done: false },
                { num: 3, label: "التأكيد", icon: "ri-checkbox-circle-line", done: false },
              ].map((step, idx) => (
                <div key={step.num} className="flex items-center gap-2">
                  <div className={`flex items-center gap-2 ${step.done || step.num === 2 ? "text-amber-400" : "text-gray-600"}`}>
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold border-2 transition-all ${
                      step.done ? "border-amber-400 bg-amber-400 text-black" : step.num === 2 ? "border-amber-400 text-amber-400" : "border-gray-700 text-gray-600"
                    }`}>
                      {step.done ? <i className="ri-check-line text-xs"></i> : step.num}
                    </div>
                    <span className="text-xs font-medium hidden sm:block">{step.label}</span>
                  </div>
                  {idx < 2 && <div className={`w-8 md:w-16 h-px ${idx === 0 ? "bg-amber-400/40" : "bg-white/10"}`} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {successData ? (
          <OrderSuccess data={successData} />
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-6">
              <i className="ri-shopping-cart-line text-4xl text-gray-600"></i>
            </div>
            <h2 className="text-white font-bold text-xl mb-2">السلة فارغة</h2>
            <p className="text-gray-500 text-sm mb-6">جاري تحويلك للصفحة الرئيسية...</p>
            <Link
              to="/"
              className="bg-amber-400 hover:bg-amber-300 text-black font-bold px-8 py-3 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              تصفح المنتجات
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* ── تحذير المنتجات التي نفد مخزونها (فحص حي) ── */}
            {stockChecked && outOfStockIds.length > 0 && (
              <div className="lg:col-span-2 bg-red-500/10 border border-red-500/30 rounded-2xl px-5 py-5 flex flex-col gap-4" dir="rtl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-500/20 rounded-xl flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-fill text-red-400 text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-red-400 font-black text-sm mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      لا يمكن إتمام الطلب — بعض المنتجات نفد مخزونها
                    </p>
                    <p className="text-red-400/70 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      المنتجات التالية غير متوفرة حالياً. يجب إزالتها من السلة قبل إتمام الطلب:
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {items.filter(i => outOfStockIds.includes(i.id)).map(item => (
                    <div key={item.id} className="flex items-center gap-3 bg-red-500/8 border border-red-500/20 rounded-xl px-4 py-3">
                      {item.image && (
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-[#1a1a1a]">
                          <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-bold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>{item.nameAr}</p>
                        <p className="text-red-400 text-[11px] mt-0.5 font-bold flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          <i className="ri-close-circle-fill text-[10px]"></i>
                          نفد المخزون تماماً
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          removeItem(item.id);
                          setOutOfStockIds(prev => prev.filter(id => id !== item.id));
                        }}
                        className="flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 text-xs font-bold px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
                        style={{ fontFamily: "'Cairo', sans-serif" }}
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                        إزالة
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    outOfStockIds.forEach(id => removeItem(id));
                    setOutOfStockIds([]);
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-black text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-delete-bin-line"></i>
                  إزالة جميع المنتجات غير المتوفرة والمتابعة
                </button>
              </div>
            )}

            {/* Right col — Order Form */}
            <div className="order-2 lg:order-1">
              <div className="mb-6">
                <h1 className="text-white font-black text-2xl mb-1">إتمام الطلب</h1>
                <p className="text-gray-500 text-sm">أدخل بياناتك وسنتواصل معك لتأكيد الطلب</p>
              </div>
              <CustomerForm
                onSuccess={handleSuccess}
                discount={discount}
                pointsDiscount={pointsDiscount}
                onPointsDiscountChange={setPointsDiscount}
                isPlatinum={isPlatinum}
                platinumDiscountPercent={PLATINUM_DISCOUNT_PERCENT}
                redeemCoupon={redeemCoupon}
                hasOutOfStockItems={outOfStockIds.length > 0}
              />
            </div>

            {/* Left col — Order Summary */}
            <div className="order-1 lg:order-2 lg:sticky lg:top-28 lg:self-start">
              <OrderSummary discount={discount} onDiscountApply={handleDiscountApply} pointsDiscount={pointsDiscount} isPlatinum={isPlatinum} platinumDiscountPercent={PLATINUM_DISCOUNT_PERCENT} redeemCoupon={redeemCoupon} onRedeemCouponApply={setRedeemCoupon} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
